import { useCallback, useEffect, useMemo, useState } from 'react'
import { StorageKeys } from '@/lib/storage/keys'

export const PIP_CAP = 5

type RecencyMap = Record<string, number>

function readRecencyMap(): RecencyMap {
  try {
    const raw = localStorage.getItem(StorageKeys.WORKSPACE_LAST_ACTIVE_AT)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as RecencyMap
    }
  } catch {
    /* ignore */
  }
  return {}
}

function writeRecencyMap(map: RecencyMap): void {
  try {
    localStorage.setItem(StorageKeys.WORKSPACE_LAST_ACTIVE_AT, JSON.stringify(map))
  } catch {
    /* ignore */
  }
}

/** MRU ordering for workspace pip glyph (trickle-up). */
export function useWorkspaceRecency(activeWorkspaceIds: string[]) {
  const [recency, setRecency] = useState<RecencyMap>(() => readRecencyMap())

  const touchWorkspace = useCallback((workspaceId: string) => {
    setRecency((prev) => {
      const next = { ...prev, [workspaceId]: Date.now() }
      writeRecencyMap(next)
      return next
    })
  }, [])

  useEffect(() => {
    const now = Date.now()
    if (activeWorkspaceIds.length === 0) return
    setRecency((prev) => {
      let changed = false
      const next = { ...prev }
      for (const id of activeWorkspaceIds) {
        if (!next[id]) {
          next[id] = now
          changed = true
        }
      }
      if (changed) writeRecencyMap(next)
      return changed ? next : prev
    })
  }, [activeWorkspaceIds])

  const orderIdsByRecency = useCallback(
    (ids: string[]) =>
      [...ids].sort((a, b) => (recency[b] ?? 0) - (recency[a] ?? 0)),
    [recency],
  )

  const pipDisplay = useMemo(() => {
    const orderedActive = orderIdsByRecency(activeWorkspaceIds)
    const visible = orderedActive.slice(0, PIP_CAP)
    const overflow = Math.max(0, orderedActive.length - PIP_CAP)
    return { visibleIds: visible, overflow }
  }, [activeWorkspaceIds, orderIdsByRecency])

  return { touchWorkspace, orderIdsByRecency, pipDisplay, PIP_CAP }
}
